import { useEffect, useMemo, useRef, useState } from "react";
import {
  HK_ONLY_ROLE,
  REGION_LOCKED_CATEGORIES,
  REGION_LOCKED_CATEGORY,
  type User,
  type UserRegion,
  type UserRoleOption,
  type UserStatus,
} from "@/services/users";
import { docCategoryApi, type DocTaxonomyItem } from "@/services/docTaxonomy";
import SourceFormPageLayout, {
  SourceFormField,
} from "@/pages/admin/settings/components/SourceFormPageLayout";
import { ADMIN_ROLE } from "@/utils/user";
import AdminGeneralSourceTypeAccess from "./AdminGeneralSourceTypeAccess";

interface AddUserModalProps {
  onClose: () => void;
  onAdd: (user: User) => void | Promise<void>;
  /** 可分配角色，来自后端 FrameworkRole 表 */
  roleOptions: UserRoleOption[];
  submitting?: boolean;
  serverError?: string | null;
}

export default function AddUserModal({
  onClose,
  onAdd,
  roleOptions,
  submitting = false,
  serverError,
}: AddUserModalProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [region, setRegion] = useState<UserRegion>("Mainland");
  const [roles, setRoles] = useState<string[]>([]);
  const [status, setStatus] = useState<UserStatus>("Active");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [multiSelectOpen, setMultiSelectOpen] = useState(false);
  const [multiSelectSearch, setMultiSelectSearch] = useState("");
  const multiSelectRef = useRef<HTMLDivElement>(null);
  // 保留完整目录对象：UI 按名称工作，提交时用同一份快照换回 ID
  const [categoryCatalog, setCategoryCatalog] = useState<DocTaxonomyItem[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  // 全员可见分类由服务端动态授权：表单中作为锁定项展示，但不重复保存到单个用户。
  const globallyVisibleCategoryNames = useMemo(
    () => categoryCatalog.filter((c) => c.is_visible_to_all_users).map((c) => c.name),
    [categoryCatalog],
  );
  const assignableCategories = categoryCatalog.filter((c) => !c.is_visible_to_all_users);
  const docCategories = assignableCategories.map((c) => c.name);

  const isHk = region === "HK";
  const isAdmin = roles.some((role) => role.toLowerCase() === ADMIN_ROLE.toLowerCase());
  // HK 用户仅可选 Staff；Mainland 展示全部 6 个角色
  const availableRoleOptions =
    isHk && roleOptions.some((r) => r.Code === HK_ONLY_ROLE)
      ? roleOptions.filter((r) => r.Code === HK_ONLY_ROLE)
      : roleOptions;
  // 该区域默认选中且不可取消的分类：Mainland → General Docs，HK → HK General Docs
  const lockedCategory = REGION_LOCKED_CATEGORY[region];
  const lockedCategoryNames = new Set([lockedCategory, ...globallyVisibleCategoryNames]);
  const [permissions, setPermissions] = useState<string[]>([REGION_LOCKED_CATEGORY.Mainland]);
  const currentPermissions = permissions;
  // 排除全部区域的锁定分类：本区域的已默认选中且不可取消，
  // 另一区域的（如 Mainland 用户看到 HK General Docs）不该出现在可选项里
  const selectableTypes = docCategories.filter((c) => !REGION_LOCKED_CATEGORIES.has(c));
  const filteredOptions = multiSelectSearch.trim()
    ? selectableTypes.filter((o) => o.toLowerCase().includes(multiSelectSearch.toLowerCase()))
    : selectableTypes;

  useEffect(() => {
    let cancelled = false;

    async function fetchCategories() {
      setCategoriesLoading(true);
      try {
        const categories = await docCategoryApi.list();

        if (!cancelled) {
          setCategoryCatalog(categories);
        }
      } catch {
        if (!cancelled) setCategoryCatalog([]);
      } finally {
        if (!cancelled) setCategoriesLoading(false);
      }
    }

    fetchCategories();

    return () => {
      cancelled = true;
    };
  }, []);

  // 分类目录加载完成后，将动态全员授权投影为默认选中、不可取消的标签。
  useEffect(() => {
    if (globallyVisibleCategoryNames.length === 0) return;
    setPermissions((current) => [...new Set([...current, ...globallyVisibleCategoryNames])]);
  }, [globallyVisibleCategoryNames]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (multiSelectRef.current && !multiSelectRef.current.contains(e.target as Node)) {
        setMultiSelectOpen(false);
        setMultiSelectSearch("");
      }
    };

    if (multiSelectOpen) {
      document.addEventListener("mousedown", handler);
    }

    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, [multiSelectOpen]);

  useEffect(() => {
    if (!isAdmin) return;
    setMultiSelectOpen(false);
    setMultiSelectSearch("");
  }, [isAdmin]);

  const handleRegionChange = (r: UserRegion) => {
    setRegion(r);
    // HK 仅允许 Staff，切过去时直接收窄已选角色
    if (r === "HK" && roleOptions.some((role) => role.Code === HK_ONLY_ROLE)) {
      setRoles([HK_ONLY_ROLE]);
    }
    // 权限重置为该区域锁定分类与全员可见分类；普通逐用户选择不跨区域沿用。
    setPermissions([...new Set([REGION_LOCKED_CATEGORY[r], ...globallyVisibleCategoryNames])]);
  };

  const handleSourceTypeToggle = (val: string) => {
    if (lockedCategoryNames.has(val)) return;
    setPermissions((prev) => (prev.includes(val) ? prev.filter((x) => x !== val) : [...prev, val]));
  };

  const handleRemovePermission = (val: string) => {
    if (lockedCategoryNames.has(val)) return;
    handleSourceTypeToggle(val);
  };

  const toggleRole = (r: string) => {
    setRoles((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));
    if (errors.roles) setErrors((prev) => ({ ...prev, roles: "" }));
  };

  const validate = () => {
    const next: Record<string, string> = {};
    // Email 是 Manual 用户的登录身份键（SSO 回跳按它匹配预录入账号），必填且需合法；
    // 唯一性由后端校验，冲突时以 serverError 形式回显
    if (!email.trim()) {
      next.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      next.email = "Please enter a valid email address";
    }
    if (roles.length === 0) {
      next.roles = "At least one role is required";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleAdd = () => {
    if (!validate()) return;
    // 用本地时区日期，避免 toISOString() 按 UTC 导致跨 0 点显示前一天
    const now = new Date();
    const nowStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    // 名称换回 ID：选项与已选值同源于目录快照，故映射必然命中
    const idByName = new Map(assignableCategories.map((c) => [c.name, c.id]));
    const newUser: User = {
      id: `u-manual-${Date.now()}`,
      name: name.trim(),
      email: email.trim(),
      source: "manual",
      sources: ["manual"],
      roles,
      status,
      region,
      docCategoryIds: currentPermissions
        .map((n) => idByName.get(n))
        .filter((id): id is string => !!id),
      studyCodes: [],
      lastActive: "-",
      createdAt: nowStr,
    };
    onAdd(newUser);
  };

  return (
    <SourceFormPageLayout
      title="Add User"
      onBack={onClose}
      primaryLabel="Add"
      primaryDisabled={submitting}
      submitting={submitting}
      onPrimary={handleAdd}
    >
      {/* <p className="text-xs text-gray-400 lg:ml-[184px]">Manually create a user account</p> */}
      <div className="flex flex-col gap-5">
        {serverError && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600 lg:ml-[184px]">
            <i className="ri-error-warning-line mt-0.5 shrink-0"></i>
            <span>{serverError}</span>
          </div>
        )}

        {/* Email */}
        <SourceFormField label="Email" required>
          <input
            type="text"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (errors.email) setErrors((prev) => ({ ...prev, email: "" }));
            }}
            placeholder="e.g. john.smith@astrazeneca.net"
            className={`w-full rounded-lg border px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-gray-400 ${
              errors.email ? "border-red-300 bg-red-50" : "border-gray-200"
            }`}
          />
          {errors.email && <p className="text-[11px] text-red-500 mt-1">{errors.email}</p>}
        </SourceFormField>

        {/* Name */}
        <SourceFormField label="Name">
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (errors.name) setErrors((prev) => ({ ...prev, name: "" }));
            }}
            placeholder="e.g. Smith, John"
            className={`w-full rounded-lg border px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-gray-400 ${
              errors.name ? "border-red-300 bg-red-50" : "border-gray-200"
            }`}
          />
          {errors.name && <p className="text-[11px] text-red-500 mt-1">{errors.name}</p>}
        </SourceFormField>

        {/* Region */}
        <SourceFormField label="Region" required>
          <div className="flex gap-2">
            {(["Mainland", "HK"] as UserRegion[]).map((r) => {
              const active = region === r;
              const label = r === "Mainland" ? "Mainland China" : "Hong Kong";
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => handleRegionChange(r)}
                  className="flex-1 flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm transition-all text-left cursor-pointer"
                  style={{
                    borderColor: active ? "#830051" : "#e5e7eb",
                    backgroundColor: active ? "rgba(131,0,81,0.06)" : "#fafafa",
                    color: active ? "#830051" : "#374151",
                  }}
                >
                  <span
                    className="w-4 h-4 flex items-center justify-center rounded-full border shrink-0 transition-colors"
                    style={{
                      borderColor: active ? "#830051" : "#d1d5db",
                      backgroundColor: active ? "#830051" : "transparent",
                    }}
                  >
                    {active && (
                      <i className="ri-check-line text-white" style={{ fontSize: "10px" }}></i>
                    )}
                  </span>
                  <span className="font-medium text-xs">{label}</span>
                </button>
              );
            })}
          </div>
        </SourceFormField>

        {/* Roles */}
        <SourceFormField label="Roles" required>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {availableRoleOptions.map((role) => {
              const r = role.Code;
              const active = roles.includes(r);
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => toggleRole(r)}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm cursor-pointer transition-all text-left"
                  style={{
                    borderColor: active ? "#830051" : "#e5e7eb",
                    backgroundColor: active ? "rgba(131,0,81,0.06)" : "#fafafa",
                    color: active ? "#830051" : "#374151",
                  }}
                >
                  <span
                    className="w-4 h-4 flex items-center justify-center rounded border shrink-0 transition-colors"
                    style={{
                      borderColor: active ? "#830051" : "#d1d5db",
                      backgroundColor: active ? "#830051" : "transparent",
                    }}
                  >
                    {active && (
                      <i className="ri-check-line text-white" style={{ fontSize: "10px" }}></i>
                    )}
                  </span>
                  <div className="flex flex-col">
                    <span className="font-medium text-xs">{role.Name}</span>
                    <span className="text-[10px] opacity-70 whitespace-normal leading-tight">
                      {role.Remark}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
          {errors.roles && <p className="text-[11px] text-red-500 mt-1.5">{errors.roles}</p>}
        </SourceFormField>

        {/* General Source Type */}
        <SourceFormField label="General Source Type">
          {isAdmin ? (
            <AdminGeneralSourceTypeAccess />
          ) : categoriesLoading ? (
            <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
              <div className="w-3.5 h-3.5 border-2 border-gray-200 border-t-[#830051] rounded-full animate-spin"></div>
              Loading categories...
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0 relative" ref={multiSelectRef}>
                <button
                  type="button"
                  onClick={() => setMultiSelectOpen((v) => !v)}
                  className="w-full min-h-[36px] px-2 py-1.5 border border-gray-200 rounded-lg flex items-center flex-wrap gap-1 cursor-pointer hover:border-gray-300 transition-colors bg-white"
                  style={multiSelectOpen ? { borderColor: "#830051" } : {}}
                >
                  {currentPermissions.map((perm) => {
                    const isLocked = lockedCategoryNames.has(perm);
                    return (
                      <span
                        key={perm}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md border ${
                          isLocked
                            ? "bg-gray-100 text-gray-600 border-gray-200"
                            : "bg-pink-50 text-[#830051] border-pink-100"
                        }`}
                      >
                        {perm}
                        {isLocked ? (
                          <span
                            className="w-3.5 h-3.5 flex items-center justify-center"
                            title={`${perm} is always enabled`}
                          >
                            <i
                              className="ri-lock-line text-gray-400"
                              style={{ fontSize: "10px" }}
                            ></i>
                          </span>
                        ) : (
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemovePermission(perm);
                            }}
                            className="w-3.5 h-3.5 flex items-center justify-center rounded hover:bg-pink-200 cursor-pointer"
                          >
                            <i className="ri-close-line" style={{ fontSize: "10px" }}></i>
                          </span>
                        )}
                      </span>
                    );
                  })}
                  <i
                    className={`ri-arrow-down-s-line text-gray-400 text-sm ml-auto transition-transform ${
                      multiSelectOpen ? "rotate-180" : ""
                    }`}
                  ></i>
                </button>

                {multiSelectOpen && (
                  <div
                    className="absolute z-30 mt-1 w-full overflow-hidden rounded-lg border border-gray-200 bg-white"
                    style={{ boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}
                  >
                    <div className="px-2 pt-2 pb-1">
                      <div className="flex items-center gap-1.5 px-2 py-1.5 border border-gray-200 rounded-md bg-white">
                        <i className="ri-search-line text-gray-400 text-xs"></i>
                        <input
                          type="text"
                          value={multiSelectSearch}
                          onChange={(e) => setMultiSelectSearch(e.target.value)}
                          placeholder="Search..."
                          className="flex-1 text-sm outline-none bg-transparent text-gray-700 placeholder-gray-400 min-w-0"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Escape") {
                              setMultiSelectOpen(false);
                              setMultiSelectSearch("");
                            }
                          }}
                        />
                        {multiSelectSearch && (
                          <button
                            type="button"
                            onClick={() => setMultiSelectSearch("")}
                            className="shrink-0 cursor-pointer"
                          >
                            <i className="ri-close-line text-gray-400 text-xs"></i>
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="max-h-44 overflow-y-auto">
                      {filteredOptions.length === 0 && (
                        <div className="px-3 py-2.5 text-xs text-gray-400">
                          No matching categories
                        </div>
                      )}
                      {filteredOptions.map((opt) => {
                        const selected = currentPermissions.includes(opt);
                        return (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => handleSourceTypeToggle(opt)}
                            className="w-full px-3 py-2 text-sm text-left cursor-pointer hover:bg-pink-50 transition-colors flex items-center gap-2"
                          >
                            <span
                              className="w-4 h-4 flex items-center justify-center rounded border shrink-0 transition-colors"
                              style={{
                                borderColor: selected ? "#830051" : "#d1d5db",
                                backgroundColor: selected ? "#830051" : "transparent",
                              }}
                            >
                              {selected && (
                                <i
                                  className="ri-check-line text-white"
                                  style={{ fontSize: "10px" }}
                                ></i>
                              )}
                            </span>
                            <span
                              className={selected ? "text-[#830051] font-medium" : "text-gray-700"}
                            >
                              {opt}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {!isAdmin && (
            <p className="text-[11px] text-gray-400 mt-2">
              {lockedCategory} is always enabled for this region. Manage categories in Admin →
              Source Settings → Doc Categories.
            </p>
          )}
        </SourceFormField>

        {/* Status */}
        <SourceFormField label="Status">
          <div className="flex min-h-9 items-center gap-2.5">
            {/* Toggle switch */}
            <button
              type="button"
              onClick={() => setStatus(status === "Active" ? "Disabled" : "Active")}
              className="relative h-5 w-9 rounded-full transition-colors duration-200 cursor-pointer shrink-0 focus:outline-none"
              style={{ backgroundColor: status === "Active" ? "#16a34a" : "#dc2626" }}
              aria-label={`Current status: ${status}. Click to toggle.`}
            >
              <span
                className={`absolute top-0.5 left-0.5 h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                  status === "Active" ? "translate-x-4" : "translate-x-0"
                }`}
              />
            </button>

            {/* Status display */}
            <div className="flex items-center gap-1.5">
              <span
                className="w-1.5 h-1.5 rounded-full shrink-0"
                style={{ backgroundColor: status === "Active" ? "#16a34a" : "#dc2626" }}
              />
              <span
                className="text-xs font-medium whitespace-nowrap"
                style={{ color: status === "Active" ? "#16a34a" : "#dc2626" }}
              >
                {status}
              </span>
            </div>
          </div>
        </SourceFormField>
      </div>
    </SourceFormPageLayout>
  );
}
